import { TProduct } from './CoreProductDetail';
import {
    TAvailabilityModel,
    TAvailabilityMessages,
    TInStockAvailability,
    TNotAvailable,
    TBackOrderAvailability,
    TPreorderAvailability
} from 'types/models/product';
import { TProductDeliveryOptionsInstance } from './ProductDeliveryOptions';

/**
 * @description Core ProductAvailabilityMixin implementation
 * <br>This class is not intended to have a separate DOM representation.
 * @param ProductAvailabilityMixin Base widget for extending
 * @returns Product availability mixin widget
 */
function CoreProductAvailabilityMixinClassCreator(
    ProductAvailabilityMixin: import('widgets/product/ProductAvailabilityMixin').TProductAvailabilityMixin
) {
    /**
     * @category widgets
     * @subcategory product
     * @class CoreProductAvailabilityMixin
     * @augments ProductAvailabilityMixin
     * @classdesc Represents product availability component with the next features:
     * 1. Gets and calculates availability model
     * 2. Gets and calculates quantity stepper attributes
     *
     * The main goal of this mixin is to avoid server requests on every quantity change. Initially gets AvailabilityLevels for maximum allowed quantity.
     * On quantity change or variation change recalculates availability status for current product quantity and renders availability component on PDP with correct status and message.
     * Also, it updates the attribute for quantity stepper.
     * @property {string} data-low-stock-class - Class using in case product low stock
     * @property {string} data-in-stock-class - Class using in case product in stock
     * @example <caption>Example HTML structure for ProductAvailabilityMixin</caption>
     * <div
     *      data-ref="availabilityData"
     *      hidden
     *      data-availability-model="${JSON.stringify(pdict.product.availability)}"
     *      data-ready-to-order="${pdict.product.readyToOrder}"
     *      data-is-inaccessible-variant="${pdict.product.isInaccessibleVariant}"
     *      data-product-available="${pdict.product.available}"
     *      data-msgs='{
     *          "reachedLimitError": "<isprint value="${Resource.msg('availability.isreachedlimit.error', 'cart', null)}" encoding="on" />",
     *          "singleInStockItemMsg": "${Resource.msg('availability.isreachedlimit.msg.single', 'cart', null)}",
     *          "multipleInStockItemsMsg": "${Resource.msg('availability.isreachedlimit.msg.multiple', 'cart', null)}",
     *          "backOrderMessage": "${Resource.msg('label.back.order', 'common', null)}",
     *          "backOrderItemsMessage": "${Resource.msg('availability.back.order.items', 'cart', null)}",
     *          "preOrderMessage": "${Resource.msg('label.preorder', 'common', null)}",
     *          "preOrderItemsMessage": "${Resource.msg('availability.pre.order.items', 'cart', null)}",
     *          "lowStockMessage": "${Resource.msg('availability.lowStock', 'product', null)}",
     *          "inStockMessage": "${Resource.msg('label.instock', 'common', null)}",
     *          "outOfStockMessage": "${Resource.msg('availability.outofstock.msg', 'cart', null)}"
     *      }'
     * ></div>
     */
    class CoreProductAvailabilityMixin extends ProductAvailabilityMixin {
        quantityWidgetId = 'quantity';

        product = {
            ...(this.product || {}),
            availability: {} as TAvailabilityModel,
            readyToOrder: false,
            isInaccessibleVariant: false,
            productAvailable: false
        };

        init() {
            super.init();

            this.getById('productDeliveryOptions', (productDeliveryOptions: TProductDeliveryOptionsInstance) => {
                productDeliveryOptions.productAvailabilityModel = this.getAvailabilityModel(productDeliveryOptions.productAvailabilityModel, 1);
            });
        }

        /**
         * @description Get in stock availability data
         * @param availabilityModel Product availability model
         * @param productQuantity Selected product quantity
         * @returns In stock availability
         */
        getInStockAvailability(availabilityModel: TAvailabilityModel, productQuantity: number): TInStockAvailability {
            const result = {} as TInStockAvailability;

            if (!availabilityModel || !productQuantity) {
                return result;
            }

            const inStockQuantity = availabilityModel?.availabilityLevelsForMaxAllowedQty?.inStock || 0;
            const quantityAvailableToSell = availabilityModel.quantityAvailableToSell || 0;
            const lowStockThreshold = availabilityModel.lowStockThreshold || 0;
            const maxQtyAllowed = availabilityModel.maxQtyAllowed;
            const availabilityMessages = this.ref('availabilityData').data<TAvailabilityMessages>('msgs');

            if (inStockQuantity === 0) {
                return result;
            }

            result.stock = inStockQuantity;
            result.useConsolidatedAvailability = availabilityModel.useConsolidatedAvailability;
            result.consolidatedAvailability = availabilityModel.consolidatedAvailability;

            if (
                quantityAvailableToSell &&
                productQuantity <= quantityAvailableToSell &&
                lowStockThreshold > 0 &&
                quantityAvailableToSell < lowStockThreshold
            ) {
                result.isLowStock = true;
                result.class = this.prefs().lowStockClass;
                result.lowStockMessage = (availabilityMessages?.lowerThanThresholdMsg ?? '').replace('{0}', String(lowStockThreshold));
            } else if (inStockQuantity >= productQuantity && productQuantity <= maxQtyAllowed) {
                result.isInStock = true;
                result.class = this.prefs().inStockClass;
                result.inStockMessage = availabilityMessages?.inStockMessage;
            } else {
                result.class = this.prefs().lowStockClass;

                if (inStockQuantity < productQuantity || productQuantity > maxQtyAllowed) {
                    result.isReachedLimit = true;
                    result.isMaxQtyAllowedReached = productQuantity >= maxQtyAllowed;
                    result.isReachedLimitMessage = availabilityMessages?.reachedLimitError;
                    result.inStockMsg = this.getInStockMessage(inStockQuantity, availabilityMessages);
                }
            }

            return result;
        }

        /**
         * @description Get non available availability data
         * @param availabilityModel Product availability model
         * @param productQuantity Selected product quantity
         * @returns Non available availability data
         */
        getNotAvailable(availabilityModel: TAvailabilityModel, productQuantity: number): TNotAvailable {
            const notAvailable = availabilityModel?.availabilityLevelsForMaxAllowedQty?.notAvailable;
            const availabilityMessages = this.ref('availabilityData').data<TAvailabilityMessages>('msgs');
            const notAvailableQuantity = notAvailable >= productQuantity ? productQuantity : notAvailable;
            const result = {} as TNotAvailable;
            const notAvailableResult = {
                class: 'm-outofstock',
                outOfStockMessage: availabilityMessages?.outOfStockMessage,
                isOutOfStock: true
            };

            if (availabilityModel.isOutOfStock) {
                return notAvailableResult;
            } else if (notAvailableQuantity > 0) {
                if (
                    notAvailableQuantity === productQuantity &&
                    availabilityModel.availabilityLevelsForMaxAllowedQty.inStock === 0 &&
                    availabilityModel.availabilityLevelsForMaxAllowedQty.backOrder === 0 &&
                    availabilityModel.availabilityLevelsForMaxAllowedQty.preOrder === 0
                ) {
                    return notAvailableResult;
                }
            }

            return result;
        }

        /**
         * @description Get backorder availability data
         * @param availabilityModel Product availability model
         * @param productQuantity Selected product quantity
         * @returns Backorder availability data
         */
        getBackOrderAvailability(availabilityModel: TAvailabilityModel, productQuantity: number): TBackOrderAvailability {
            const backOrder = availabilityModel?.availabilityLevelsForMaxAllowedQty?.backOrder || 0;
            const availabilityMessages = this.ref('availabilityData').data<TAvailabilityMessages>('msgs');
            const backOrderQuantity = backOrder >= productQuantity ? productQuantity : backOrder;
            const result = {} as TBackOrderAvailability;

            if (
                backOrderQuantity > 0 &&
                availabilityModel.availabilityLevelsForMaxAllowedQty.inStock === 0 &&
                availabilityModel.availabilityLevelsForMaxAllowedQty.preOrder === 0
            ) {
                result.isBackOrder = true;

                if (productQuantity <= backOrderQuantity) {
                    result.class = this.prefs().inStockClass;
                    result.backOrderMessage = availabilityMessages?.backOrderMessage;
                } else {
                    result.class = this.prefs().lowStockClass;
                    result.isReachedLimit = true;
                    result.isReachedLimitMessage = availabilityMessages?.reachedLimitError;
                    result.backOrderMessage = availabilityMessages?.backOrderItemsMessage?.replace('#', String(productQuantity));
                }
            }

            return result;
        }

        /**
         * @description Get preorder availability data
         * @param availabilityModel Product availability model
         * @param productQuantity Selected product quantity
         * @returns Preorder availability data
         */
        getPreorderAvailability(availabilityModel: TAvailabilityModel, productQuantity: number): TPreorderAvailability {
            const preOrder = availabilityModel?.availabilityLevelsForMaxAllowedQty?.preOrder || 0;
            const availabilityMessages = this.ref('availabilityData').data<TAvailabilityMessages>('msgs');
            const preOrderQuantity = preOrder >= productQuantity ? productQuantity : preOrder;
            const result = {} as TPreorderAvailability;

            if (
                preOrderQuantity > 0 &&
                availabilityModel.availabilityLevelsForMaxAllowedQty.inStock === 0 &&
                availabilityModel.availabilityLevelsForMaxAllowedQty.backOrder === 0
            ) {
                result.isPreOrder = true;

                if (productQuantity <= preOrderQuantity) {
                    result.class = this.prefs().inStockClass;
                    result.preOrderMessage = availabilityMessages?.preOrderMessage;
                } else {
                    result.class = this.prefs().lowStockClass;
                    result.isReachedLimit = true;
                    result.isReachedLimitMessage = availabilityMessages?.reachedLimitError;
                    result.preOrderMessage = availabilityMessages?.preOrderItemsMessage?.replace('#', String(productQuantity));
                }
            }

            return result;
        }

        /**
         * @description Get quantity stepper attributes for current product quantity
         * @param availabilityModel Product availability model
         * @param minOrderQuantity Minimum product order quantity
         * @param stepQuantity Quantity stepper step
         * @returns Quantity stepper attributes
         */
        getQuantityStepperAttributes(availabilityModel: TAvailabilityModel, minOrderQuantity: number, stepQuantity: number) {
            const quantityAvailableToSell = availabilityModel ? availabilityModel.quantityAvailableToSell : null;
            const maxQtyAllowed = availabilityModel.maxQtyAllowed || 0;
            const selectedQty = this.getSelectedQuantity();
            const minOrderQty = minOrderQuantity;
            const stepQty = stepQuantity;
            const currentQty = selectedQty && selectedQty > minOrderQty ? selectedQty : minOrderQty;
            const max =
                availabilityModel && quantityAvailableToSell && quantityAvailableToSell < maxQtyAllowed ? quantityAvailableToSell + 1 : maxQtyAllowed;

            return {
                max: max.toFixed(),
                min: minOrderQty.toFixed(),
                step: stepQty.toFixed(),
                currentQty: currentQty.toFixed()
            };
        }

        /**
         * @description Render product delivery options and availability
         * @param product Product object
         */
        renderAvailability(product: TProduct) {
            this.has('productAvailabilityMsg', productAvailabilityMsg => {
                productAvailabilityMsg.hide();
            });

            if (!product.bopis?.available) {
                this.getById('availability', availabilityLabel => {
                    availabilityLabel?.show?.();
                    availabilityLabel?.render?.(
                        'template',
                        {
                            availability: this.product.availability,
                            productAvailable: product.available,
                            bopis: product.bopis
                        },
                        availabilityLabel?.ref?.('container')
                    );
                });
            } else {
                this.getById('availability', availabilityLabel => {
                    availabilityLabel?.hide?.();
                });
            }

            return this.getById('productDeliveryOptions', (productDeliveryOptions: TProductDeliveryOptionsInstance) => {
                return productDeliveryOptions.update(product);
            });
        }

        /**
         * @description Combines different availability statuses into a single availability model.
         * @param availabilityModel The availability model of the product.
         * @param productQuantity The quantity of the product being considered.
         * @returns A combined availability model including in-stock, not available, backorder, and preorder statuses.
         */
        getAvailabilityModel(availabilityModel: TAvailabilityModel, productQuantity: number) {
            return {
                ...this.getInStockAvailability(availabilityModel, productQuantity),
                ...this.getNotAvailable(availabilityModel, productQuantity),
                ...this.getBackOrderAvailability(availabilityModel, productQuantity),
                ...this.getPreorderAvailability(availabilityModel, productQuantity),
                availabilityLevelsForMaxAllowedQty: availabilityModel.availabilityLevelsForMaxAllowedQty,
                quantityAvailableToSell: availabilityModel.quantityAvailableToSell,
                lowStockThreshold: availabilityModel.lowStockThreshold,
                maxQtyAllowed: availabilityModel.maxQtyAllowed,
                hasAvailableVariant: availabilityModel.hasAvailableVariant
            };
        }
    }

    return CoreProductAvailabilityMixin;
}

export type TCoreProductAvailabilityMixin = ReturnType<typeof CoreProductAvailabilityMixinClassCreator>;

export type TCoreProductAvailabilityMixinInstance = InstanceType<TCoreProductAvailabilityMixin>;

export default CoreProductAvailabilityMixinClassCreator;
